import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, Image, Wand2, Palette, Sparkles, Languages, Trash2, 
  Plus, ChevronUp, ChevronDown, Save, Eye, Layers, FolderPlus,
  Check, X, GripVertical, Zap, BookOpen, Send, Download, RefreshCw,
  ImagePlus, FileText, Undo2, RotateCcw, ArrowRight,
  Copy, Grid3X3, List, Search, Filter, Clock, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
  colorizeImage, styleTransfer, enhancePanel, translateText, generateCover,
  getArtStyles, getEnhancementOptions, createComic, createEpisode, getComics,
  getGenres
} from '../services/api';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import { 
  ComparisonModal, 
  CoverGenerationModal, 
  ProcessingOverlay, 
  OnboardingTips,
  EpisodeManager
} from '../components/creator-studio';

// AI Tool configurations
const AI_TOOLS = [
  { id: 'colorize', name: 'Colorize', icon: Palette, color: 'text-pink-500', bgColor: 'bg-pink-500/10', desc: 'Add colors to B&W art' },
  { id: 'enhance', name: 'Enhance', icon: Sparkles, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', desc: 'Improve quality & details' },
  { id: 'style', name: 'Style Transfer', icon: Wand2, color: 'text-purple-500', bgColor: 'bg-purple-500/10', desc: 'Apply anime/manga styles' },
  { id: 'cover', name: 'Generate Cover', icon: ImagePlus, color: 'text-green-500', bgColor: 'bg-green-500/10', desc: 'AI-generated cover art' },
];

const STYLE_PRESETS = [
  { id: 'ghibli', name: 'Studio Ghibli', description: 'Soft watercolor, warm lighting' },
  { id: 'aot', name: 'Attack on Titan', description: 'Dark dramatic shading' },
  { id: 'one_piece', name: 'One Piece', description: 'Bold vibrant colors' },
  { id: 'demon_slayer', name: 'Demon Slayer', description: 'Fluid motion, gradients' },
  { id: 'jjk', name: 'Jujutsu Kaisen', description: 'Dynamic, supernatural glow' },
  { id: 'webtoon', name: 'Modern Webtoon', description: 'Clean digital polish' },
];

const GENRES = [
  { value: 'action', label: 'Action' },
  { value: 'romance', label: 'Romance' },
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'horror', label: 'Horror' },
  { value: 'comedy', label: 'Comedy' },
  { value: 'drama', label: 'Drama' },
  { value: 'sci-fi', label: 'Sci-Fi' },
  { value: 'slice-of-life', label: 'Slice of Life' },
];

// Local storage key for auto-save
const WORKSPACE_STORAGE_KEY = 'supremics_creator_studio_workspace';

// Helper function to get badge styling based on image type
const getImageTypeBadge = (type) => {
  const badges = {
    cover: { color: 'bg-green-500', label: 'Cover' },
    colorize: { color: 'bg-pink-500', label: 'AI: colorize' },
    enhance: { color: 'bg-yellow-500', label: 'AI: enhance' },
    style: { color: 'bg-purple-500', label: 'AI: style' },
  };
  return badges[type] || { color: 'bg-blue-500', label: `AI: ${type}` };
};

// Helper function to get rank badge styling
const getRankBadgeStyle = (index) => {
  if (index === 0) return 'bg-[#FFB703] text-black';
  if (index === 1) return 'bg-gray-300 text-black';
  if (index === 2) return 'bg-amber-600 text-white';
  return 'bg-[var(--surface)] text-[var(--text-secondary)]';
};

function ComicCreatorStudio() {
  const { user, isCreator } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Workspace state
  const [workspace, setWorkspace] = useState([]); // All images in workspace
  const [episodes, setEpisodes] = useState([{ id: 1, name: 'Episode 1', panels: [] }]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [activeEpisode, setActiveEpisode] = useState(1);
  const [workspaceView, setWorkspaceView] = useState('grid'); // grid | list
  
  // AI processing state
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  
  // Comic metadata
  const [comicTitle, setComicTitle] = useState('');
  const [comicDescription, setComicDescription] = useState('');
  const [comicGenre, setComicGenre] = useState('action');
  const [coverImage, setCoverImage] = useState(null); // Generated or uploaded cover
  
  // Existing comics for adding episodes
  const [existingComics, setExistingComics] = useState([]);
  const [selectedComic, setSelectedComic] = useState(null); // null = new comic, or comic object
  const [isNewComic, setIsNewComic] = useState(true);
  
  // AI tool settings
  const [colorDescription, setColorDescription] = useState('Vibrant anime colors with soft shadows');
  const [selectedStyle, setSelectedStyle] = useState('ghibli');
  const [enhanceSettings, setEnhanceSettings] = useState(['cleanup', 'colors']);
  const [activeTool, setActiveTool] = useState(null);
  
  // Cover generation settings
  const [coverTitle, setCoverTitle] = useState('');
  const [coverDescription, setCoverDescription] = useState('');
  const [coverStyleNotes, setCoverStyleNotes] = useState('');
  
  // View mode
  const [viewMode, setViewMode] = useState('workspace'); // workspace | organize | preview
  
  // Comparison view
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonImages, setComparisonImages] = useState({ original: null, processed: null });
  
  // Onboarding
  const [showTips, setShowTips] = useState(true);
  
  // History for undo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Load saved workspace on mount
  useEffect(() => {
    const saved = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Restore text fields
        if (data.comicTitle) setComicTitle(data.comicTitle);
        if (data.comicDescription) setComicDescription(data.comicDescription);
        if (data.comicGenre) setComicGenre(data.comicGenre);
        
        // Note: We only saved metadata, not images
        // Images need to be re-uploaded, but we can restore episode structure
        if (data.episodes?.length > 0) {
          const restoredEpisodes = data.episodes.map(ep => ({
            id: ep.id,
            name: ep.name,
            panels: [] // Panels need to be re-added
          }));
          if (restoredEpisodes.length > 0) {
            setEpisodes(restoredEpisodes);
          }
        }
        
        // Show message that images need to be re-uploaded
        if (data.workspaceMetadata?.length > 0) {
          toast.info(`Session restored. Re-upload your ${data.workspaceMetadata.length} image(s) to continue.`);
        }
      } catch (e) {
        console.error('Failed to restore workspace:', e);
        // Clear corrupted data
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      }
    }
  }, []);
  
  // Auto-save workspace (with error handling and size limits)
  useEffect(() => {
    try {
      // Only save metadata, not full base64 images (to avoid quota issues)
      // Full images will be re-uploaded on page refresh
      const saveData = {
        // Save workspace with truncated previews
        workspaceMetadata: workspace.map(img => ({
          id: img.id,
          name: img.name,
          type: img.type,
          processed: img.processed,
          addedAt: img.addedAt
          // Note: base64/preview not saved to avoid quota issues
        })),
        episodes: episodes.map(ep => ({
          id: ep.id,
          name: ep.name,
          panelIds: ep.panels.map(p => p.id) // Only save IDs, not full panel data
        })),
        comicTitle,
        comicDescription,
        comicGenre,
        savedAt: new Date().toISOString()
      };
      
      const dataStr = JSON.stringify(saveData);
      // Only save if under 1MB to be safe
      if (dataStr.length < 1000000) {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, dataStr);
      } else {
        console.warn('Workspace too large to auto-save to localStorage');
      }
    } catch (e) {
      // Handle quota exceeded or other storage errors silently
      console.warn('Auto-save failed:', e.message);
      // Try to clear old data to make room
      try {
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      } catch (clearError) {
        console.debug('Failed to clear workspace storage:', clearError.message);
      }
    }
  }, [workspace, episodes, comicTitle, comicDescription, comicGenre]);
  
  // Fetch user's existing comics
  useEffect(() => {
    async function fetchUserComics() {
      try {
        const res = await getComics({ limit: 50 });
        // Filter to only show user's comics
        const userComics = res.data.filter(c => c.creator_id === user?.id);
        setExistingComics(userComics);
      } catch (error) {
        console.error('Failed to fetch comics:', error);
      }
    }
    if (user?.id) {
      fetchUserComics();
    }
  }, [user?.id]);

  // Handle file upload
  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    processFiles(files);
  }, []);

  const processFiles = (files) => {
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          base64: e.target.result.split(',')[1],
          preview: e.target.result,
          type: 'original',
          processed: false,
          addedAt: new Date().toISOString()
        };
        setWorkspace(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Toggle image selection
  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  // Select all images
  const selectAllImages = () => {
    if (selectedImages.length === workspace.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(workspace.map(img => img.id));
    }
  };
  
  // Clear workspace
  const clearWorkspace = () => {
    if (workspace.length === 0 && episodes.every(ep => ep.panels.length === 0)) return;
    
    if (window.confirm('Are you sure you want to clear the entire workspace? This cannot be undone.')) {
      setWorkspace([]);
      setEpisodes([{ id: 1, name: 'Episode 1', panels: [] }]);
      setSelectedImages([]);
      setCoverImage(null);
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      toast.success('Workspace cleared');
    }
  };
  
  // Generate AI Cover
  const handleGenerateCover = async () => {
    if (!coverTitle && !comicTitle) {
      toast.error('Enter a title for cover generation');
      return;
    }
    
    setProcessing(true);
    setProcessingMessage('AI is generating your cover... (may take up to 90 seconds)');
    setProcessingProgress(10);
    
    try {
      const res = await generateCover(
        coverTitle || comicTitle,
        coverDescription || comicDescription || 'An exciting comic story',
        comicGenre,
        coverStyleNotes
      );
      
      if (res.data?.image_base64) {
        const newCover = {
          id: `cover_${Date.now()}`,
          name: 'AI Generated Cover',
          base64: res.data.image_base64,
          preview: `data:${res.data.mime_type || 'image/png'};base64,${res.data.image_base64}`,
          type: 'cover',
          processed: true,
          addedAt: new Date().toISOString()
        };
        setCoverImage(newCover);
        setWorkspace(prev => [...prev, newCover]);
        toast.success('Cover generated successfully!');
      }
    } catch (error) {
      console.error('Cover generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate cover');
    } finally {
      setProcessing(false);
      setProcessingMessage('');
      setProcessingProgress(0);
    }
  };

  // Apply AI tool to selected images
  const applyAITool = async (tool) => {
    if (tool === 'cover') {
      setActiveTool('cover');
      return;
    }
    
    if (selectedImages.length === 0) {
      toast.error('Select images first');
      return;
    }

    setProcessing(true);
    const selectedImgs = workspace.filter(img => selectedImages.includes(img.id));
    let processed = 0;
    const total = selectedImgs.length;

    for (const img of selectedImgs) {
      try {
        setProcessingMessage(`Processing ${++processed}/${total}: ${img.name}`);
        setProcessingProgress(Math.round((processed / total) * 100));
        
        let result;
        if (tool === 'colorize') {
          const res = await colorizeImage(img.base64, colorDescription);
          result = res.data;
        } else if (tool === 'enhance') {
          const res = await enhancePanel(img.base64, enhanceSettings, true);
          result = res.data;
        } else if (tool === 'style') {
          const res = await styleTransfer(img.base64, selectedStyle, 'medium');
          result = res.data;
        }

        if (result?.image_base64) {
          const newImage = {
            id: `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: `${tool}_${img.name}`,
            base64: result.image_base64,
            preview: `data:${result.mime_type || 'image/png'};base64,${result.image_base64}`,
            type: tool,
            processed: true,
            sourceId: img.id,
            addedAt: new Date().toISOString()
          };
          setWorkspace(prev => [...prev, newImage]);
          
          // Enable comparison
          setComparisonImages({ original: img, processed: newImage });
        }
      } catch (error) {
        console.error(`Failed to process ${img.name}:`, error);
        toast.error(`Failed: ${img.name}`);
      }
    }

    setProcessing(false);
    setProcessingMessage('');
    setProcessingProgress(0);
    setSelectedImages([]);
    toast.success(`Processed ${processed} images`);
    
    // Show comparison option
    if (processed > 0) {
      setComparisonMode(true);
    }
  };

  // Add image to episode
  const addToEpisode = (imageId, episodeId) => {
    setEpisodes(prev => prev.map(ep => {
      if (ep.id === episodeId) {
        const image = workspace.find(img => img.id === imageId);
        if (image && !ep.panels.find(p => p.id === imageId)) {
          return { ...ep, panels: [...ep.panels, { ...image, order: ep.panels.length }] };
        }
      }
      return ep;
    }));
  };

  // Remove image from episode
  const removeFromEpisode = (imageId, episodeId) => {
    setEpisodes(prev => prev.map(ep => {
      if (ep.id === episodeId) {
        return { ...ep, panels: ep.panels.filter(p => p.id !== imageId) };
      }
      return ep;
    }));
  };

  // Add new episode
  const addEpisode = () => {
    const newId = Math.max(...episodes.map(e => e.id)) + 1;
    setEpisodes(prev => [...prev, { id: newId, name: `Episode ${newId}`, panels: [] }]);
    setActiveEpisode(newId);
  };

  // Reorder panel within episode
  const reorderPanel = (episodeId, panelId, direction) => {
    setEpisodes(prev => prev.map(ep => {
      if (ep.id === episodeId) {
        const panels = [...ep.panels];
        const index = panels.findIndex(p => p.id === panelId);
        if (index === -1) return ep;
        
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= panels.length) return ep;
        
        [panels[index], panels[newIndex]] = [panels[newIndex], panels[index]];
        return { ...ep, panels };
      }
      return ep;
    }));
  };

  // Delete image from workspace
  const deleteImage = (imageId) => {
    setWorkspace(prev => prev.filter(img => img.id !== imageId));
    setSelectedImages(prev => prev.filter(id => id !== imageId));
    // Also remove from episodes
    setEpisodes(prev => prev.map(ep => ({
      ...ep,
      panels: ep.panels.filter(p => p.id !== imageId)
    })));
  };

  // Publish comic
  const publishComic = async () => {
    const episodesWithPanels = episodes.filter(ep => ep.panels.length > 0);
    
    if (episodesWithPanels.length === 0) {
      toast.error('Add panels to at least one episode');
      return;
    }

    // For new comics, require title
    if (isNewComic && !comicTitle) {
      toast.error('Enter a comic title');
      return;
    }
    
    // For existing comics, must have selected one
    if (!isNewComic && !selectedComic) {
      toast.error('Select an existing comic');
      return;
    }

    setProcessing(true);
    setProcessingMessage('Publishing your comic...');
    setProcessingProgress(5);

    try {
      let comicId;
      
      if (isNewComic) {
        // Create new comic
        setProcessingMessage('Creating new comic...');
        const comicRes = await createComic({
          title: comicTitle,
          description: comicDescription,
          genre: comicGenre,
          cover_image: coverImage?.preview || '',
          status: 'ongoing'
        });
        comicId = comicRes.data.id;
      } else {
        comicId = selectedComic.id;
      }

      // Create each episode
      for (let i = 0; i < episodesWithPanels.length; i++) {
        const ep = episodesWithPanels[i];
        const progress = Math.round(((i + 1) / episodesWithPanels.length) * 90) + 10;
        setProcessingMessage(`Publishing Episode ${i + 1}/${episodesWithPanels.length}...`);
        setProcessingProgress(progress);

        // Convert panels to the format expected by the API (list of data URLs)
        const panelUrls = ep.panels.map(panel => panel.preview);

        await createEpisode(comicId, {
          title: ep.name,
          episode_number: isNewComic ? i + 1 : (selectedComic?.episode_count || 0) + i + 1,
          panels: panelUrls
        });
      }

      toast.success('Comic published successfully!');
      
      // Clear workspace
      setWorkspace([]);
      setEpisodes([{ id: 1, name: 'Episode 1', panels: [] }]);
      setComicTitle('');
      setComicDescription('');
      setCoverImage(null);
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      
      // Navigate to the comic
      navigate(`/comic/${comicId}`);
      
    } catch (error) {
      console.error('Publish error:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setProcessing(false);
      setProcessingMessage('');
      setProcessingProgress(0);
    }
  };
  
  // Download image
  const downloadImage = (img) => {
    const link = document.createElement('a');
    link.href = img.preview;
    link.download = `${img.name || 'image'}.png`;
    link.click();
  };
  
  // Duplicate image
  const duplicateImage = (img) => {
    const newImage = {
      ...img,
      id: `dup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `copy_${img.name}`,
      addedAt: new Date().toISOString()
    };
    setWorkspace(prev => [...prev, newImage]);
    toast.success('Image duplicated');
  };

  const activeEpisodeData = episodes.find(ep => ep.id === activeEpisode);
  
  // Calculate total panels across all episodes
  const totalPanels = episodes.reduce((sum, ep) => sum + ep.panels.length, 0);

  // Check if user is not a creator
  if (!isCreator && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center page-padding">
        <div className="text-center max-w-md">
          <Zap size={64} className="mx-auto mb-6 text-[var(--primary)]" />
          <h1 className="text-3xl font-teko mb-4">Creator Access Required</h1>
          <p className="text-[var(--text-secondary)] mb-6">
            The Comic Creator Studio is available for Creator tier subscribers.
          </p>
          <a href="/pricing" className="btn-hotrod inline-block">
            <span>Upgrade to Creator</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="comic-creator-studio">
      <SEO title="Comic Creator Studio" description="Create and publish comics with AI-powered tools" />

      {/* Header */}
      <div className="sticky top-16 z-30 bg-[var(--background)]/95 backdrop-blur-md border-b border-[var(--border)]">
        <div className="page-padding py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#E63946] to-[#FF6B35] rounded-lg flex items-center justify-center shrink-0">
                <Zap size={20} className="text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-teko font-bold leading-tight">Comic Creator Studio</h1>
                <p className="text-xs text-[var(--text-secondary)]">
                  {workspace.length} images • {totalPanels} panels ready
                </p>
              </div>
            </div>
            
            {/* View Mode Tabs */}
            <div className="flex items-center gap-1 bg-[var(--surface)] p-1 rounded-lg">
              <button
                onClick={() => setViewMode('workspace')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === 'workspace' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-secondary)] hover:text-white'
                }`}
                data-testid="tab-workspace"
              >
                <Layers size={14} />
                <span className="hidden sm:inline">Workspace</span>
              </button>
              <button
                onClick={() => setViewMode('organize')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === 'organize' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-secondary)] hover:text-white'
                }`}
                data-testid="tab-organize"
              >
                <BookOpen size={14} />
                <span className="hidden sm:inline">Organize</span>
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  viewMode === 'preview' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-secondary)] hover:text-white'
                }`}
                data-testid="tab-preview"
              >
                <Eye size={14} />
                <span className="hidden sm:inline">Preview</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={clearWorkspace}
                className="p-2 text-[var(--text-secondary)] hover:text-[var(--error)] transition-colors"
                title="Clear workspace"
              >
                <RotateCcw size={18} />
              </button>
              <button
                onClick={publishComic}
                disabled={processing || episodes.every(ep => ep.panels.length === 0)}
                className="btn-hotrod px-4 py-2 flex items-center gap-2 disabled:opacity-50 text-sm"
                data-testid="publish-comic-btn"
              >
                <Send size={16} />
                <span className="hidden sm:inline">Publish</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Overlay */}
      <ProcessingOverlay 
        isProcessing={processing}
        message={processingMessage}
        progress={processingProgress}
      />
      
      {/* Comparison Modal */}
      <ComparisonModal
        comparisonImages={comparisonImages?.original && comparisonImages?.processed ? {
          original: comparisonImages.original.preview,
          processed: comparisonImages.processed.preview
        } : null}
        onDownload={downloadImage}
        onClose={() => setComparisonMode(false)}
      />
      
      {/* Cover Generation Modal */}
      <CoverGenerationModal
        isOpen={activeTool === 'cover'}
        onClose={() => setActiveTool(null)}
        coverTitle={coverTitle}
        setCoverTitle={setCoverTitle}
        coverDescription={coverDescription}
        setCoverDescription={setCoverDescription}
        coverStyleNotes={coverStyleNotes}
        setCoverStyleNotes={setCoverStyleNotes}
        comicTitle={comicTitle}
        comicDescription={comicDescription}
        onGenerate={handleGenerateCover}
      />

      <div className="page-padding py-6">
        {/* Onboarding Tips */}
        <OnboardingTips
          show={showTips}
          onDismiss={() => setShowTips(false)}
          workspaceEmpty={workspace.length === 0}
          isWorkspaceView={viewMode === 'workspace'}
        />
        
        {/* WORKSPACE VIEW */}
        {viewMode === 'workspace' && (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Left: Upload & AI Tools */}
            <div className="lg:col-span-1 space-y-4">
              {/* Upload Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-[var(--border)] rounded-xl p-6 text-center hover:border-[var(--primary)] transition-colors cursor-pointer bg-[var(--surface)]/50"
              >
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  ref={fileInputRef}
                  data-testid="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload size={32} className="mx-auto text-[var(--primary)] mb-3" />
                  <p className="font-medium">Drop images here</p>
                  <p className="text-sm text-[var(--text-secondary)]">or click to browse</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-2">PNG, JPG, WEBP supported</p>
                </label>
              </div>

              {/* AI Tools */}
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
                <h3 className="font-teko text-lg mb-3 flex items-center gap-2">
                  <Wand2 size={18} className="text-[var(--primary)]" />
                  AI Tools
                  <span className="ml-auto text-xs text-[var(--text-secondary)] font-normal">
                    {selectedImages.length > 0 ? `${selectedImages.length} selected` : 'Select images'}
                  </span>
                </h3>
                
                <div className="space-y-2 mb-4">
                  {AI_TOOLS.map(tool => (
                    <button
                      key={tool.id}
                      onClick={() => applyAITool(tool.id)}
                      disabled={(tool.id !== 'cover' && selectedImages.length === 0) || processing}
                      className={`w-full flex items-center gap-3 p-3 ${tool.bgColor} border border-[var(--border)] rounded-lg hover:border-[var(--primary)] transition-all disabled:opacity-50 disabled:cursor-not-allowed group`}
                      data-testid={`ai-tool-${tool.id}`}
                    >
                      <tool.icon size={20} className={`${tool.color} group-hover:scale-110 transition-transform`} />
                      <div className="text-left flex-1">
                        <p className="font-medium text-sm">{tool.name}</p>
                        <p className="text-xs text-[var(--text-secondary)]">{tool.desc}</p>
                      </div>
                      <ArrowRight size={14} className="text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>

                {/* Tool Settings */}
                <div className="space-y-3 pt-3 border-t border-[var(--border)]">
                  <div>
                    <label className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                      <Palette size={12} /> Color Style (for Colorize)
                    </label>
                    <input
                      type="text"
                      value={colorDescription}
                      onChange={(e) => setColorDescription(e.target.value)}
                      placeholder="Describe colors..."
                      className="w-full mt-1 px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
                      <Wand2 size={12} /> Art Style (for Style Transfer)
                    </label>
                    <select
                      value={selectedStyle}
                      onChange={(e) => setSelectedStyle(e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
                    >
                      {STYLE_PRESETS.map(s => (
                        <option key={s.id} value={s.id}>{s.name} - {s.description}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Selection Info */}
              {selectedImages.length > 0 && (
                <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[var(--primary)]">
                      {selectedImages.length} image(s) selected
                    </p>
                    <button
                      onClick={() => setSelectedImages([])}
                      className="text-xs text-[var(--text-secondary)] hover:text-white px-2 py-1 bg-[var(--background)] rounded"
                    >
                      Clear
                    </button>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    Ready for AI processing
                  </p>
                </div>
              )}
              
              {/* Quick Stats */}
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
                <h4 className="text-sm font-medium mb-3">Workspace Stats</h4>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="p-2 bg-[var(--background)] rounded-lg">
                    <p className="text-xl font-bold text-[var(--primary)]">{workspace.length}</p>
                    <p className="text-xs text-[var(--text-secondary)]">Images</p>
                  </div>
                  <div className="p-2 bg-[var(--background)] rounded-lg">
                    <p className="text-xl font-bold text-green-500">{workspace.filter(i => i.processed).length}</p>
                    <p className="text-xs text-[var(--text-secondary)]">AI Processed</p>
                  </div>
                  <div className="p-2 bg-[var(--background)] rounded-lg">
                    <p className="text-xl font-bold text-blue-500">{episodes.length}</p>
                    <p className="text-xs text-[var(--text-secondary)]">Episodes</p>
                  </div>
                  <div className="p-2 bg-[var(--background)] rounded-lg">
                    <p className="text-xl font-bold text-purple-500">{totalPanels}</p>
                    <p className="text-xs text-[var(--text-secondary)]">Panels</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Image Grid */}
            <div className="lg:col-span-3">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-teko text-xl">
                    Workspace
                  </h3>
                  <span className="text-sm text-[var(--text-secondary)]">
                    {workspace.length} images
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={selectAllImages}
                    className="text-sm text-[var(--text-secondary)] hover:text-[var(--primary)] px-3 py-1 border border-[var(--border)] rounded-lg"
                  >
                    {selectedImages.length === workspace.length && workspace.length > 0 ? 'Deselect All' : 'Select All'}
                  </button>
                  <div className="flex items-center bg-[var(--surface)] rounded-lg p-1">
                    <button
                      onClick={() => setWorkspaceView('grid')}
                      className={`p-1.5 rounded ${workspaceView === 'grid' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-secondary)]'}`}
                    >
                      <Grid3X3 size={16} />
                    </button>
                    <button
                      onClick={() => setWorkspaceView('list')}
                      className={`p-1.5 rounded ${workspaceView === 'list' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-secondary)]'}`}
                    >
                      <List size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {workspace.length === 0 ? (
                <div className="bg-[var(--surface)] border border-dashed border-[var(--border)] rounded-xl p-12 text-center">
                  <Image size={48} className="mx-auto text-[var(--text-secondary)] mb-4" />
                  <p className="text-[var(--text-secondary)] font-medium">No images yet</p>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 mb-4">Upload images to get started</p>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg inline-flex items-center gap-2"
                  >
                    <Upload size={16} /> Upload Images
                  </button>
                </div>
              ) : (
                <div className={workspaceView === 'grid' 
                  ? "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4"
                  : "space-y-2"
                }>
                  {workspace.map(img => (
                    <div
                      key={img.id}
                      onClick={() => toggleImageSelection(img.id)}
                      className={workspaceView === 'grid' 
                        ? `relative group bg-[var(--surface)] border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                          selectedImages.includes(img.id)
                            ? 'border-[var(--primary)] ring-2 ring-[var(--primary)]/30 scale-[1.02]'
                            : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                        }`
                        : `flex items-center gap-4 p-3 bg-[var(--surface)] border-2 rounded-lg cursor-pointer transition-all ${
                          selectedImages.includes(img.id)
                            ? 'border-[var(--primary)] ring-2 ring-[var(--primary)]/30'
                            : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                        }`
                      }
                      data-testid={`workspace-image-${img.id}`}
                    >
                      {workspaceView === 'grid' ? (
                        <>
                          <div className="aspect-[3/4] relative">
                            <img
                              src={img.preview}
                              alt={img.name}
                              className="w-full h-full object-cover"
                            />
                            {/* Selection checkbox */}
                            <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              selectedImages.includes(img.id)
                                ? 'bg-[var(--primary)] border-[var(--primary)]'
                                : 'bg-black/50 border-white/50'
                            }`}>
                              {selectedImages.includes(img.id) && <Check size={14} className="text-white" />}
                            </div>
                            {/* Type badge */}
                            {img.type !== 'original' && (
                              <div className={`absolute top-2 right-2 px-2 py-0.5 text-white text-xs rounded-full ${getImageTypeBadge(img.type).color}`}>
                                {getImageTypeBadge(img.type).label}
                              </div>
                            )}
                            {/* Actions */}
                            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); addToEpisode(img.id, activeEpisode); }}
                                  className="flex-1 px-2 py-1.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 flex items-center justify-center gap-1"
                                  title="Add to episode"
                                >
                                  <Plus size={12} /> Ep.{activeEpisode}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); downloadImage(img); }}
                                  className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                                  title="Download"
                                >
                                  <Download size={12} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); duplicateImage(img); }}
                                  className="p-1.5 bg-purple-500 text-white rounded hover:bg-purple-600"
                                  title="Duplicate"
                                >
                                  <Copy size={12} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteImage(img.id); }}
                                  className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                                  title="Delete"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="p-2">
                            <p className="text-xs truncate">{img.name}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                            selectedImages.includes(img.id)
                              ? 'bg-[var(--primary)] border-[var(--primary)]'
                              : 'border-[var(--border)]'
                          }`}>
                            {selectedImages.includes(img.id) && <Check size={14} className="text-white" />}
                          </div>
                          <img src={img.preview} alt={img.name} className="w-16 h-20 object-cover rounded shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{img.name}</p>
                            <p className="text-xs text-[var(--text-secondary)]">
                              {img.type !== 'original' ? `AI: ${img.type}` : 'Original'} • Added {new Date(img.addedAt).toLocaleTimeString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); addToEpisode(img.id, activeEpisode); }} className="p-2 hover:bg-green-500/20 rounded text-green-500">
                              <Plus size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); downloadImage(img); }} className="p-2 hover:bg-blue-500/20 rounded text-blue-500">
                              <Download size={16} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); deleteImage(img.id); }} className="p-2 hover:bg-red-500/20 rounded text-red-500">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ORGANIZE VIEW */}
        {viewMode === 'organize' && (
          <EpisodeManager
            episodes={episodes}
            activeEpisode={activeEpisode}
            setActiveEpisode={setActiveEpisode}
            addEpisode={addEpisode}
            updateEpisodeName={(episodeId, name) => {
              setEpisodes(prev => prev.map(ep =>
                ep.id === episodeId ? { ...ep, name } : ep
              ));
            }}
            workspace={workspace}
            addToEpisode={addToEpisode}
            activeEpisodeData={activeEpisodeData}
            reorderPanel={reorderPanel}
            removeFromEpisode={removeFromEpisode}
          />
        )}

        {/* PREVIEW VIEW */}
        {viewMode === 'preview' && (
          <div className="max-w-4xl mx-auto">
            {/* Comic Target Selection */}
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 mb-6">
              <h3 className="font-teko text-xl mb-4">Publish To</h3>
              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => { setIsNewComic(true); setSelectedComic(null); }}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    isNewComic 
                      ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                      : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                  }`}
                >
                  <FolderPlus size={24} className={`mx-auto mb-2 ${isNewComic ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'}`} />
                  <p className="font-medium">New Comic</p>
                  <p className="text-xs text-[var(--text-secondary)]">Create a brand new comic</p>
                </button>
                <button
                  onClick={() => setIsNewComic(false)}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    !isNewComic 
                      ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                      : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                  }`}
                >
                  <BookOpen size={24} className={`mx-auto mb-2 ${!isNewComic ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'}`} />
                  <p className="font-medium">Existing Comic</p>
                  <p className="text-xs text-[var(--text-secondary)]">Add episodes to your comic</p>
                </button>
              </div>
              
              {!isNewComic && (
                <div>
                  <label className="text-sm text-[var(--text-secondary)]">Select Your Comic</label>
                  <select
                    value={selectedComic?.id || ''}
                    onChange={(e) => {
                      const comic = existingComics.find(c => c.id === e.target.value);
                      setSelectedComic(comic || null);
                    }}
                    className="w-full mt-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                  >
                    <option value="">-- Select a comic --</option>
                    {existingComics.map(comic => (
                      <option key={comic.id} value={comic.id}>
                        {comic.title} ({comic.episode_count || 0} episodes)
                      </option>
                    ))}
                  </select>
                  {existingComics.length === 0 && (
                    <p className="text-xs text-[var(--text-secondary)] mt-2">
                      You do not have any comics yet. Create a new one!
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Comic Metadata (for new comics) */}
            {isNewComic && (
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 mb-6">
                <h3 className="font-teko text-xl mb-4">Comic Details</h3>
                
                {/* Cover Preview */}
                <div className="flex gap-6 mb-6">
                  <div className="shrink-0">
                    {coverImage ? (
                      <div className="relative">
                        <img 
                          src={coverImage.preview} 
                          alt="Cover" 
                          className="w-32 h-44 object-cover rounded-lg border border-[var(--border)]"
                        />
                        <button
                          onClick={() => setCoverImage(null)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setActiveTool('cover')}
                        className="w-32 h-44 border-2 border-dashed border-[var(--border)] rounded-lg flex flex-col items-center justify-center text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                      >
                        <ImagePlus size={24} className="mb-2" />
                        <span className="text-xs text-center">Generate<br/>AI Cover</span>
                      </button>
                    )}
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <label className="text-sm text-[var(--text-secondary)]">Title *</label>
                      <input
                        type="text"
                        value={comicTitle}
                        onChange={(e) => setComicTitle(e.target.value)}
                        placeholder="Enter comic title"
                        className="w-full mt-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                        data-testid="comic-title-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[var(--text-secondary)]">Genre</label>
                      <select
                        value={comicGenre}
                        onChange={(e) => setComicGenre(e.target.value)}
                        className="w-full mt-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      >
                        {GENRES.map(g => (
                          <option key={g.value} value={g.value}>{g.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-[var(--text-secondary)]">Description</label>
                  <textarea
                    value={comicDescription}
                    onChange={(e) => setComicDescription(e.target.value)}
                    placeholder="Describe your comic story..."
                    rows={3}
                    className="w-full mt-1 px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg resize-none"
                  />
                </div>
              </div>
            )}

            {/* Episodes Preview */}
            <div className="space-y-6">
              <h3 className="font-teko text-xl">
                Episodes to Publish ({episodes.filter(ep => ep.panels.length > 0).length})
              </h3>
              
              {episodes.filter(ep => ep.panels.length > 0).map((ep, idx) => (
                <div key={ep.id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-teko text-lg flex items-center gap-2">
                      <CheckCircle2 className="text-green-500" size={18} />
                      {ep.name}
                    </h4>
                    <span className="text-sm text-[var(--text-secondary)]">
                      {ep.panels.length} panels
                    </span>
                  </div>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {ep.panels.map((panel, panelIdx) => (
                      <div key={panel.id} className="relative aspect-[3/4] rounded overflow-hidden border border-[var(--border)]">
                        <img src={panel.preview} alt="" className="w-full h-full object-cover" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-center text-xs py-0.5">
                          {panelIdx + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {episodes.every(ep => ep.panels.length === 0) && (
                <div className="text-center py-12 bg-[var(--surface)] border border-dashed border-[var(--border)] rounded-xl">
                  <BookOpen size={48} className="mx-auto text-[var(--text-secondary)] mb-4" />
                  <p className="text-[var(--text-secondary)]">No episodes ready</p>
                  <p className="text-sm text-[var(--text-secondary)] mt-1">
                    Go to <button onClick={() => setViewMode('organize')} className="text-[var(--primary)] underline">Organize tab</button> to add panels to episodes
                  </p>
                </div>
              )}
            </div>
            
            {/* Publish Button */}
            {episodes.some(ep => ep.panels.length > 0) && (
              <div className="mt-8 p-6 bg-gradient-to-r from-[var(--primary)]/10 to-[var(--secondary)]/10 border border-[var(--primary)]/20 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-teko text-xl">Ready to Publish?</h4>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {isNewComic ? `Creating "${comicTitle || 'Untitled'}" with ` : `Adding to "${selectedComic?.title || 'selected comic'}" with `}
                      {episodes.filter(ep => ep.panels.length > 0).length} episode(s)
                    </p>
                  </div>
                  <button
                    onClick={publishComic}
                    disabled={processing || (isNewComic && !comicTitle) || (!isNewComic && !selectedComic)}
                    className="btn-hotrod px-8 py-3 flex items-center gap-2 disabled:opacity-50"
                    data-testid="final-publish-btn"
                  >
                    <Send size={18} />
                    Publish Now
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ComicCreatorStudio;
